import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loading, Avatar } from "../components/common";
import { toast } from "sonner";
import { Dumbbell, Play, Trash2, Target, Ghost, TrendingUp, Dices } from "lucide-react";

const RTC_LABELS = { easy: "Easy", normal: "Normal", hard: "Hard" };

export default function Practice() {
  const [players, setPlayers] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [progress, setProgress] = useState(null);
  const [rtcProgress, setRtcProgress] = useState(null);
  const [rtcHeatmap, setRtcHeatmap] = useState(null);
  const [difficulty, setDifficulty] = useState("easy");
  const [selected, setSelected] = useState("");
  const [gameType, setGameType] = useState("501");
  const [mode, setMode] = useState("solo");
  const [bestOf, setBestOf] = useState(1);
  const [ghostPlayer, setGhostPlayer] = useState("");
  const [startingGhost, setStartingGhost] = useState(false);
  const [startRule, setStartRule] = useState("straight_in");
  const [finishRule, setFinishRule] = useState("double_out");
  const [starting, setStarting] = useState(false);
  const [goals, setGoals] = useState({ avg_501: "", hit_pct_atw: "" });
  const [savingGoals, setSavingGoals] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const [p, s, pr] = await Promise.all([
        api.get("/players"),
        api.get("/practice"),
        api.get("/practice/progress"),
      ]);
      setPlayers(p.data);
      setSessions(s.data);
      setProgress(pr.data);
      try {
        const rtc = (await api.get("/practice/rtc-progress")).data;
        setRtcProgress(rtc);
        setDifficulty(rtc.recommended || "easy");
        setRtcHeatmap((await api.get("/practice/rtc-heatmap")).data);
      } catch {}
      if (!selected && p.data[0]) setSelected(p.data[0].id);
    } catch (e) {
      if (e.response?.status !== 401) toast.error("Could not load practice. Please retry.");
      setPlayers([]);
    }
  };
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!players || !selected) return;
    const g = players.find((p) => p.id === selected)?.goals || {};
    setGoals({
      avg_501: g.avg_501 != null ? String(g.avg_501) : "",
      hit_pct_atw: g.hit_pct_atw != null ? String(g.hit_pct_atw) : "",
    });
  }, [selected, players]);

  if (players === null) return <Loading label="Loading practice" />;

  const start = async () => {
    if (!selected) return toast.error("Pick a player");
    setStarting(true);
    try {
      const { data } = await api.post("/practice", {
        player_id: selected,
        game_type: gameType,
        start_rule: startRule,
        finish_rule: finishRule,
        ...(gameType === "random_target" ? { difficulty } : {}),
      });
      navigate(`/practice/${data.id}`);
    } catch {
      toast.error("Could not start session");
      setStarting(false);
    }
  };

  const startGhost = async () => {
    if (!selected) return toast.error("Pick a player");
    const gp = ghostPlayer || selected;
    setStartingGhost(true);
    try {
      const { data } = await api.get(`/ghosts/${gp}?game_type=${gameType}`);
      if (!data.length) {
        const gpName = players.find((p) => p.id === gp)?.name || "That player";
        toast.error(`${gpName} has no ${gameType === "baseball" ? "Baseball" : "501"} games yet — play some first to unlock their Ghost.`);
        setStartingGhost(false);
        return;
      }
      const player = players.find((p) => p.id === selected);
      const ghostP = players.find((p) => p.id === gp);
      navigate("/ghost", {
        state: {
          playerId: selected,
          playerName: player?.name || "You",
          ghostName: gp === selected ? null : ghostP?.name || "Ghost",
          gameType,
          bestOf,
          startRule,
          finishRule,
          ghosts: data,
        },
      });
    } catch {
      toast.error("Could not load ghosts");
      setStartingGhost(false);
    }
  };

  const remove = async (s, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this practice session?")) return;
    await api.delete(`/practice/${s.id}`);
    toast.success("Session deleted");
    load();
  };

  const saveGoals = async () => {
    if (!selected) return toast.error("Pick a player");
    setSavingGoals(true);
    try {
      await api.put(`/players/${selected}/goals`, {
        avg_501: goals.avg_501 === "" ? null : Number(goals.avg_501),
        hit_pct_atw: goals.hit_pct_atw === "" ? null : Number(goals.hit_pct_atw),
      });
      toast.success("Goals saved");
      load();
    } catch {
      toast.error("Could not save goals");
    } finally {
      setSavingGoals(false);
    }
  };

  const nameOf = (id) => players.find((p) => p.id === id)?.name || "?";

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF3B30] mb-2">Solo</div>
        <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter text-white leading-none">
          Practice
        </h1>
        <p className="text-slate-400 mt-2 text-sm">
          {mode === "ghost"
            ? "Race a past self — your own or any league player's. Ghost games are practice only — they never touch wins, rivalries or stats."
            : "Sharpen your game solo — 501, Around the World, Baseball or 9-Dart High Score. Practice is tracked separately from your rivalry record."}
        </p>
      </div>

      <div className="panel rounded-sm p-6">
        <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">New Session</h2>
        {players.length === 0 ? (
          <p className="text-slate-500">
            Add a player first on the{" "}
            <button onClick={() => navigate("/players")} className="text-[#007AFF] underline">
              Players
            </button>{" "}
            page.
          </p>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-1">
              <RuleBtn active={mode === "solo"} onClick={() => setMode("solo")} testid="prac-mode-solo">Solo Practice</RuleBtn>
              <RuleBtn
                active={mode === "ghost"}
                onClick={() => {
                  setMode("ghost");
                  if (gameType === "around_the_world" || gameType === "nine_dart" || gameType === "random_target") setGameType("501");
                }}
                testid="prac-mode-ghost"
              >
                👻 Play a Ghost
              </RuleBtn>
            </div>
            {mode === "ghost" && (
              <p className="text-xs text-slate-500 -mt-1">
                Race a past self — your own or any league player's. Ghost games never change wins, rivalries or stats.
              </p>
            )}
            <div className="grid gap-1 grid-cols-2">
              <RuleBtn active={gameType === "501"} onClick={() => setGameType("501")} testid="prac-game-501">501</RuleBtn>
              {mode === "solo" && (
                <RuleBtn active={gameType === "around_the_world"} onClick={() => setGameType("around_the_world")} testid="prac-game-atw">Around the World</RuleBtn>
              )}
              <RuleBtn active={gameType === "baseball"} onClick={() => setGameType("baseball")} testid="prac-game-baseball">Baseball</RuleBtn>
              {mode === "solo" && (
                <RuleBtn active={gameType === "nine_dart"} onClick={() => setGameType("nine_dart")} testid="prac-game-ninedart">9-Dart High Score</RuleBtn>
              )}
              {mode === "solo" && (
                <RuleBtn active={gameType === "random_target"} onClick={() => setGameType("random_target")} testid="prac-game-rtc">🎲 Random Target</RuleBtn>
              )}
            </div>
            {mode === "solo" && gameType === "random_target" && (
              <div data-testid="rtc-difficulty-row">
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1 flex items-center gap-2">
                  Difficulty
                  {rtcProgress?.recommended && (
                    <span data-testid="rtc-recommended-badge" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-[#1a2a12] text-[#4ADE80] text-[10px] font-bold uppercase tracking-wide">
                      Recommended: {RTC_LABELS[rtcProgress.recommended]}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {["easy", "normal", "hard"].map((d) => (
                    <RuleBtn key={d} active={difficulty === d} onClick={() => setDifficulty(d)} testid={`rtc-diff-${d}`}>
                      {RTC_LABELS[d]}
                    </RuleBtn>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  30 darts, one random target each. Difficulty stays fixed for the whole session.
                </p>
              </div>
            )}
            {mode === "ghost" && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Ghost Player</div>
                <select
                  data-testid="ghost-player-select"
                  value={ghostPlayer || selected}
                  onChange={(e) => setGhostPlayer(e.target.value)}
                  className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#5E5CE6]"
                >
                  {players.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  Race this player's past games. Which specific game you get stays hidden until you finish.
                </p>
              </div>
            )}
            {mode === "ghost" && (
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Format</div>
                <div className="grid grid-cols-5 gap-1">
                  {[1, 3, 5, 7, 9].map((n) => (
                    <RuleBtn key={n} active={bestOf === n} onClick={() => setBestOf(n)} testid={`ghost-bo-${n}`}>
                      {n === 1 ? "Single" : `Bo${n}`}
                    </RuleBtn>
                  ))}
                </div>
              </div>
            )}
            {gameType === "501" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Starting Rule</div>
                <div className="grid grid-cols-2 gap-1">
                  <RuleBtn active={startRule === "straight_in"} onClick={() => setStartRule("straight_in")} testid="prac-start-straight">Straight In</RuleBtn>
                  <RuleBtn active={startRule === "double_in"} onClick={() => setStartRule("double_in")} testid="prac-start-double">Double In</RuleBtn>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">Finish Rule</div>
                <div className="grid grid-cols-2 gap-1">
                  <RuleBtn active={finishRule === "double_out"} onClick={() => setFinishRule("double_out")} testid="prac-finish-double">Double Out</RuleBtn>
                  <RuleBtn active={finishRule === "straight_out"} onClick={() => setFinishRule("straight_out")} testid="prac-finish-straight">Straight Out</RuleBtn>
                </div>
              </div>
            </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                data-testid="practice-player-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="flex-1 bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
              >
                {players.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <button
                data-testid="start-practice-btn"
                onClick={mode === "ghost" ? startGhost : start}
                disabled={mode === "ghost" ? startingGhost : starting}
                className={`flex items-center justify-center gap-2 px-6 py-3 disabled:opacity-50 font-bold uppercase tracking-wide rounded-sm transition-colors ${
                  mode === "ghost"
                    ? "bg-[#5E5CE6] hover:bg-[#4f4dd1] text-white"
                    : "bg-[#34C759] hover:bg-[#2fb350] text-black"
                }`}
              >
                {mode === "ghost" ? (
                  <>
                    <Ghost className="w-5 h-5" /> {startingGhost ? "Loading..." : "Play a Ghost"}
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" /> {starting ? "Starting..." : "Start Practice"}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {user?.linked_player_id && progress && (
        <div className="panel rounded-sm p-6" data-testid="practice-progress-panel">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-[#007AFF]" />
            <h2 className="font-display text-2xl font-bold uppercase text-white">Your 501 Practice Progress</h2>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Private to you — your own solo 501 practice 3-dart averages over time. Never counts toward
            wins, rivalries or leaderboards.
          </p>
          {progress.total === 0 ? (
            <p data-testid="practice-progress-empty" className="text-slate-500 text-sm">
              Complete a solo 501 practice session to start tracking your progress.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <ProgressTile testid="progress-latest" label="Latest Avg" value={progress.latest} accent="text-white" />
              <ProgressTile testid="progress-previous" label="Previous Avg" value={progress.previous ?? "—"} accent="text-slate-300" />
              <ProgressTile
                testid="progress-change"
                label="Change"
                value={progress.change == null ? "—" : progress.change > 0 ? `+${progress.change}` : `${progress.change}`}
                accent={progress.change == null ? "text-slate-400" : progress.change > 0 ? "text-[#34C759]" : progress.change < 0 ? "text-[#FF3B30]" : "text-slate-300"}
              />
              <ProgressTile testid="progress-last5" label="Last 5 Avg" value={progress.last5} accent="text-[#007AFF]" />
              <ProgressTile testid="progress-best" label="Personal Best" value={progress.personal_best} accent="text-[#FFCC00]" />
              <ProgressTile testid="progress-total" label="Total Sessions" value={progress.total} accent="text-[#34C759]" />
            </div>
          )}
        </div>
      )}

      {user?.linked_player_id && rtcProgress && (
        <div className="panel rounded-sm p-6" data-testid="rtc-progress-panel">
          <div className="flex items-center gap-2 mb-1">
            <Dices className="w-5 h-5 text-[#4ADE80]" />
            <h2 className="font-display text-2xl font-bold uppercase text-white">Random Target Progress</h2>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Private to you — your accuracy per difficulty. Recommended next level:{" "}
            <span data-testid="rtc-progress-recommended" className="text-[#4ADE80] font-bold uppercase">{RTC_LABELS[rtcProgress.recommended]}</span>.
          </p>
          <div className="grid grid-cols-3 gap-2">
            {["easy", "normal", "hard"].map((d) => {
              const lv = rtcProgress.levels?.[d] || { pb: 0, last5: 0, sessions: 0 };
              return (
                <div key={d} data-testid={`rtc-level-${d}`} className="panel rounded-sm p-3">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white mb-1">{RTC_LABELS[d]}</div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-500 text-[11px] uppercase">PB</span>
                    <span className="font-display text-lg font-black text-[#FFCC00]">{lv.pb}%</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-500 text-[11px] uppercase">Last 5</span>
                    <span className="font-display text-base font-bold text-[#007AFF]">{lv.last5}%</span>
                  </div>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-slate-500 text-[11px] uppercase">Sessions</span>
                    <span className="font-display text-base font-bold text-slate-300">{lv.sessions}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {user?.linked_player_id && rtcHeatmap && (
        <RtcHeatmap data={rtcHeatmap} />
      )}

      {players.length > 0 && (
        <div className="panel rounded-sm p-6" data-testid="goals-card">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-5 h-5 text-[#FFCC00]" />
            <h2 className="font-display text-2xl font-bold uppercase text-white">Goals</h2>
          </div>
          <p className="text-slate-400 text-sm mb-4">
            Set targets for <span className="text-white font-semibold">{nameOf(selected)}</span>. We'll
            cheer when a session beats their personal best, and show if they hit their goal.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">
                Target 3-Dart Avg (501)
              </div>
              <input
                data-testid="goal-avg-input"
                type="number"
                min="0"
                max="180"
                step="0.1"
                value={goals.avg_501}
                onChange={(e) => setGoals({ ...goals, avg_501: e.target.value })}
                placeholder="e.g. 60"
                className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
              />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1">
                Target Hit % (Around the World)
              </div>
              <input
                data-testid="goal-hitpct-input"
                type="number"
                min="0"
                max="100"
                step="1"
                value={goals.hit_pct_atw}
                onChange={(e) => setGoals({ ...goals, hit_pct_atw: e.target.value })}
                placeholder="e.g. 40"
                className="w-full bg-[#1A1A1A] border border-[#262626] rounded-sm px-4 py-3 text-white font-bold focus:outline-none focus:ring-2 focus:ring-[#FFCC00]"
              />
            </div>
          </div>
          <button
            data-testid="save-goals-btn"
            onClick={saveGoals}
            disabled={savingGoals}
            className="mt-3 px-6 py-2.5 bg-[#FFCC00] hover:bg-[#e6b800] disabled:opacity-50 text-black font-bold uppercase tracking-wide rounded-sm transition-colors"
          >
            {savingGoals ? "Saving..." : "Save Goals"}
          </button>
        </div>
      )}

      <div>
        <h2 className="font-display text-2xl font-bold uppercase text-white mb-3">Past Sessions</h2>
        {sessions.length === 0 ? (
          <p className="text-slate-500 text-sm">No practice sessions yet.</p>
        ) : (
          <div className="space-y-1">
            {sessions.map((s) => (
              <div
                key={s.id}
                data-testid={`practice-session-${s.id}`}
                onClick={() => navigate(`/practice/${s.id}`)}
                className="panel rounded-sm p-4 flex items-center gap-3 cursor-pointer hover:border-[#007AFF] transition-colors"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#12331d] rounded-sm">
                  <Dumbbell className="w-5 h-5 text-[#34C759]" />
                </div>
                <Avatar name={s.player_name || nameOf(s.player_id)} size={36} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold text-white truncate">
                    {s.player_name || nameOf(s.player_id)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {new Date(s.date).toLocaleString()} ·{" "}
                    <span className={s.status === "completed" ? "text-[#34C759]" : "text-[#FFCC00]"}>
                      {s.status === "completed" ? "Completed" : "In Progress"}
                    </span>
                  </div>
                </div>
                <button
                  data-testid={`delete-practice-${s.id}`}
                  onClick={(e) => remove(s, e)}
                  className="text-slate-600 hover:text-[#FF3B30] p-2"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RtcHeatmap({ data }) {
  const cellStyle = (n) => {
    if (!n.attempts) return { background: "#141414", borderColor: "#262626", color: "#475569" };
    const hue = (n.rate / 100) * 120; // 0=red -> 120=green
    return { background: `hsl(${hue} 58% 24%)`, borderColor: `hsl(${hue} 60% 40%)`, color: "#fff" };
  };
  const weak = [...data.numbers]
    .filter((n) => n.attempts >= 3 && n.rate != null)
    .sort((a, b) => a.rate - b.rate)
    .slice(0, 3);

  return (
    <div className="panel rounded-sm p-6" data-testid="rtc-heatmap-panel">
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-5 h-5 text-[#FF3B30]" />
        <h2 className="font-display text-2xl font-bold uppercase text-white">Target Heatmap</h2>
      </div>
      <p className="text-slate-400 text-sm mb-4">
        Private to you — hit rate per number across all your Random Target sessions. Redder = weaker,
        greener = stronger.
      </p>
      {data.total_attempts === 0 ? (
        <p data-testid="rtc-heatmap-empty" className="text-slate-500 text-sm">
          Play some Random Target sessions to build your heatmap.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1.5">
            {data.numbers.map((n) => (
              <div
                key={n.base}
                data-testid={`rtc-heat-${n.base}`}
                title={n.attempts ? `${n.label}: ${n.hits}/${n.attempts} hit` : `${n.label}: no attempts yet`}
                className="rounded-sm border p-1.5 text-center transition-colors"
                style={cellStyle(n)}
              >
                <div className="font-display text-sm font-black leading-none">{n.label}</div>
                <div className="text-[10px] font-bold mt-0.5">{n.rate != null ? `${n.rate}%` : "—"}</div>
              </div>
            ))}
          </div>
          {weak.length > 0 && (
            <div data-testid="rtc-weak-spots" className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">Weak spots</span>
              {weak.map((n) => (
                <span key={n.base} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-[#2a0f0d] border border-[#5c1c1c] text-[#FF6B6B] text-xs font-bold uppercase">
                  {n.label} · {n.rate}%
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ProgressTile({ label, value, accent = "text-white", testid }) {
  return (
    <div data-testid={testid} className="panel rounded-sm p-3 text-center">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`font-display text-2xl font-black ${accent}`}>{value}</div>
    </div>
  );
}

function RuleBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`py-2 rounded-sm font-bold uppercase text-xs tracking-wide transition-colors ${
        active ? "bg-[#007AFF] text-white" : "panel text-slate-300 hover:border-slate-600"
      }`}
    >
      {children}
    </button>
  );
}
